import type {
  CopSolution,
  EngineSolution,
  EngineTarget,
  FlowSolution,
} from "@/lib/tool-kit/physics-fields/carnot";
import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";

/** Equations with the numbers substituted; temperatures in kelvin, energies in joules. */

export function engineWorking(
  target: EngineTarget,
  s: EngineSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const pct = (v: number) => `${formatSig(fmt, v * 100, sig)}%`;
  if (target === "hot") {
    return [
      "T_h = T_c / (1 − η)",
      `T_h = ${e(s.cold)} / (1 − ${e(s.efficiency)}) = ${formatSig(fmt, s.hot, sig)} K`,
    ];
  }
  if (target === "cold") {
    return [
      "T_c = T_h (1 − η)",
      `T_c = ${e(s.hot)} × (1 − ${e(s.efficiency)}) = ${formatSig(fmt, s.cold, sig)} K`,
    ];
  }
  return [
    "η_C = 1 − T_c / T_h",
    `η_C = 1 − ${e(s.cold)} / ${e(s.hot)} = ${e(s.efficiency)} = ${pct(s.efficiency)}`,
  ];
}

export function copWorking(
  s: CopSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const lines = [
    "COP_cool = T_c / (T_h − T_c)",
    `COP_cool = ${e(s.cold)} / (${e(s.hot)} − ${e(s.cold)}) = ${formatSig(fmt, s.copCooling, sig)}`,
    "COP_heat = T_h / (T_h − T_c) = COP_cool + 1",
    `COP_heat = ${e(s.hot)} / (${e(s.hot)} − ${e(s.cold)}) = ${formatSig(fmt, s.copHeating, sig)}`,
  ];
  if (s.work !== null && s.heatRemoved !== null && s.heatDelivered !== null) {
    lines.push(
      `Q_c = COP_cool × W = ${formatSig(fmt, s.copCooling, sig)} × ${e(s.work)} = ${formatSig(fmt, s.heatRemoved, sig)} J`,
      `Q_h = COP_heat × W = ${formatSig(fmt, s.copHeating, sig)} × ${e(s.work)} = ${formatSig(fmt, s.heatDelivered, sig)} J`
    );
  }
  return lines;
}

export function flowWorking(
  s: FlowSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  return [
    `W = Q_h − Q_c = ${e(s.heatIn)} − ${e(s.heatOut)} = ${r(s.work, "J")}`,
    `η = W / Q_h = ${e(s.work)} / ${e(s.heatIn)} = ${r(s.efficiency * 100, "%")}`,
    `η_C = 1 − T_c / T_h = 1 − ${e(s.cold)} / ${e(s.hot)} = ${r(s.carnotEfficiency * 100, "%")}`,
    `W_max = η_C Q_h = ${r(s.maxWork, "J")}`,
    `ΔS = Q_c / T_c − Q_h / T_h = ${e(s.heatOut)} / ${e(s.cold)} − ${e(s.heatIn)} / ${e(s.hot)} = ${r(s.entropyGenerated, "J/K")}`,
  ];
}
